import type { ParsedUrlQuery } from 'querystring';

import cx from 'classnames';
import { useRouter } from 'next/router';
import * as React from 'react';
import { useCallback, useEffect } from 'react';

import { logger } from '../../../core/Log/logger';
import { Pagination, usePagination } from '../../../core/Pagination/Pagination';
import { LogoLoader } from '../../../design-system/components/loader/logo/LogoLoader';
import { PaginationNavigation } from '../../../design-system/components/pagination/PaginationNavigation';
import { Destination } from '../../../resources/destination/domain/types/destination';
import { getPointsOfInterestFromFront } from '../../../resources/pointOfInterest/api/front/pointOfInterest';
import { PointOfInterest } from '../../../resources/pointOfInterest/domain/types/PointOfInterest';
import { PointOfInterestCard } from '../../../shared/components/PointOfInterestCard/PointOfInterestCard';
import { useLocale } from '../../../shared/hooks/useLocale';
import { useUrlGenerator } from '../../../shared/hooks/useUrlGenerator';
import { PickDomain } from '../../../shared/utilities/types/PickDomain';
import { useCurrentPageFromRouterAndPagination } from '../../shared/useCurrentPageFromRouterAndPagination';

import style from './PointOfInterestList.module.css';

export function PointOfInterestList({
    destination,
    pointsOfInterest,
    pagination: initialPagination,
}: {
    destination: PickDomain<Destination, 'id' | 'slug'>;
    pointsOfInterest: Array<
        PickDomain<
            PointOfInterest,
            'id' | 'slug' | 'name',
            {
                description: PointOfInterest['description'] | null;
                destination: PickDomain<Destination, 'id' | 'slug'>;
                averageRating: Pick<PointOfInterest['averageRating'], 'rating'> | null;
                picture: PointOfInterest['picture'] | null;
            }
        >
    >;
    pagination: Pagination;
}) {
    const { locale } = useLocale();
    const { generatePath } = useUrlGenerator();
    const router = useRouter();
    const pagination = usePagination({
        pagination: initialPagination,
        items: pointsOfInterest,
        fetcher: paginationFetcher,
        manuallyHandlePageChangeError: true,
    });
    const currentPage = useCurrentPageFromRouterAndPagination(pagination);

    async function paginationFetcher({
        page,
        itemsPerPage,
    }: {
        itemsPerPage: number;
        page: number;
    }) {
        return getPointsOfInterestFromFront({
            locale,
            destinationId: destination.id,
            isOnline: true,
            page,
            perPage: itemsPerPage,
        });
    }

    const handlePageChange = useCallback(
        (page: number) => {
            const routeParamsNames = ['destinationSlug'];
            const pageParamName = 'p';
            const excludedParams = routeParamsNames.concat([pageParamName]);
            const routerQuery = { ...router.query };

            const queryKeys = Object.keys(routerQuery);
            const cleanQueryKeys = queryKeys.filter((key) => {
                return !excludedParams.includes(key);
            });

            const routeParamsQuery = {
                destinationSlug: destination.slug,
            };
            const nextInitQuery: ParsedUrlQuery =
                page === 1
                    ? routeParamsQuery
                    : { ...routeParamsQuery, [pageParamName]: String(page) };
            const nextQuery = cleanQueryKeys.reduce((acc, key) => {
                acc[key] = router.query[key];
                return acc;
            }, nextInitQuery);

            const nextUrl = {
                pathname: router.pathname,
                query: nextQuery,
            };

            const asInitQuery = {} as ParsedUrlQuery;
            const asQuery = Object.keys(nextQuery).reduce((acc, key) => {
                if (!routeParamsNames.includes(key)) {
                    acc[key] = nextQuery[key];
                }
                return acc;
            }, asInitQuery);

            const asUrl = {
                pathname: router.asPath.split('?')[0],
                query: asQuery,
            };

            const options = {
                shallow: true,
                scroll: true,
            };

            return router.push(nextUrl, asUrl, options).catch((error) => {
                logger.error(
                    'Router push failed on pagination navigation',
                    { routerQuery, nextUrl, asUrl, options },
                    error,
                );
            });
        },
        [router, destination.slug],
    );

    useEffect(() => {
        const previousPage = pagination.currentPage;

        if (currentPage === previousPage) {
            return;
        }

        void pagination.goToPage(currentPage).catch((error) => {
            void handlePageChange(previousPage);

            logger.error(
                'PaginationNavigation fetch failed',
                {
                    previousPage,
                    page: currentPage,
                    pagination: {
                        firstPage: pagination.firstPage,
                        previousPage: pagination.previousPage,
                        currentPage: pagination.currentPage,
                        nextPage: pagination.nextPage,
                        lastPage: pagination.lastPage,
                    },
                },
                error,
            );
        });
    }, [currentPage, pagination, handlePageChange]);

    return (
        <div>
            <div className={cx('row', style.row)}>
                {pagination.currentPageItems().map((pointOfInterest, index) => {
                    return (
                        <div
                            key={pointOfInterest.id}
                            className="col-12 col-sm-6 col-lg-4 col-xxl-3"
                        >
                            <PointOfInterestCard
                                pointOfInterest={pointOfInterest}
                                placeNameElement="h2"
                                withDescription={true}
                                trackingInformation={{
                                    click: {
                                        block: 'places',
                                        click_type: 'card',
                                        card_number: index + 1,
                                    },
                                }}
                            />
                        </div>
                    );
                })}
            </div>

            {pagination.isAnyPageLoading() ? (
                <div className={style.loaderContainer}>
                    <LogoLoader />
                </div>
            ) : null}

            {pagination.lastPage > 1 ? (
                <div className={style.paginationContainer}>
                    <PaginationNavigation
                        pagination={pagination}
                        onPageChange={handlePageChange}
                        generateUrl={(page) => {
                            return generatePath('point_of_interest_list', {
                                destinationSlug: destination.slug,
                                p: page === 1 ? undefined : page,
                            });
                        }}
                        trackingInformation={{
                            click: {
                                block: 'places',
                            },
                        }}
                    />
                </div>
            ) : null}
        </div>
    );
}
